import React from 'react';
import { useNavigate } from 'react-router-dom';
import { format, isBefore, isFuture, isSameDay, subHours } from 'date-fns';
import { toast } from 'react-toastify';
import { useReactToPrint } from 'react-to-print';
import { DepositPaymentStatus } from '@prisma/client';
import { isAxiosError } from 'axios';
import SideBarModal from '../modal/SideBarModal';
import { Appointment, FooterAction, FooterActionGroup } from '../../interfaces';
import AppointmentSidebarStatusHeader from './AppointmentSidebarStatusHeader';
import { createModalData } from '../../utils/modal/appointmentModal';
import useSizeOptions from '../../hooks/tattoos/useSizeOptions';
import useCheckoutForm from '../../hooks/global/useCheckoutForm';
import useUser from '../../hooks/global/useUser';
import useCashDiscount from '../../hooks/payments/useCashDiscount';
import useGetAppointmentPayments from '../../hooks/appointments/useGetAppointmentPayments';
import {
  checkPaymentType,
  convertPaymentsToTransactions,
  getDepositPaidExclTaxAmount,
} from '../../helpers/payments';
import TransactionRefundModal from '../transactions/TransactionRefundModal';
import usePiercingLocations from '../../hooks/piercings/usePiercingLocations';
import {
  createFooterAction,
  getFullName,
  openStorageUrl,
} from '../../helpers/global';
import {
  cancelAppointment,
  createAppointmentChat,
  invalidateAppointmentPayments,
  invalidateDepositTransferAppointments,
  markAppointmentNoShow,
  resendCheckoutLink,
  sendAppointmentReminder,
  uncancelAppointment,
} from '../../services/appointments';
import useSubscriptionModal from '../../hooks/subscriptions/useSubscriptionModal';
import useFeatureGuard from '../../hooks/subscriptions/useFeatureGuard';
import { Feature } from '../../types/subscriptions';
import {
  getAppointmentFormValues,
  getCancelAppointmentDepositOptions,
  isAppointmentDepositPaid,
  isWaiverSelectable,
  navigateToChat,
} from '../../helpers/appointments';
import {
  reactivateWaiverRequest,
  requestWaiver,
  sendWaiverReminder,
  createWaiverRequestSession,
} from '../../services/waivers';
import useGetWaivers from '../../hooks/waivers/useGetWaivers';
import useWaiverRequestsTableActions from '../../hooks/waivers/useWaiverRequestsTableActions';
import useConfirm from '../../hooks/useConfirm';
import useLoadingModal from '../../hooks/global/useLoadingModal';
import useAwaitedForm from '../../hooks/useAwaitedForm';
import {
  appointmentRefundDepositSchema,
  AppointmentRefundDepositState,
} from '../../types/appointments';
import TransferAppointmentDepositModal from './TransferAppointmentDepositModal';
import useGetAppointment from '../../hooks/appointments/useGetAppointment';
import { getBusinessShopUrl } from '../../helpers/businesses';
import { getWaiverRequestStatus } from '../../helpers/waivers';
import { ROUTES } from '../../constants/global';
import useInitialAppointmentForm from '../../hooks/appointments/useInitialAppointmentForm';
import useAppointmentFormOpen from '../../hooks/appointments/useAppointmentFormOpen';
import useCalendarSelectedAppointment from '../../hooks/appointments/useCalendarSelectedAppointment';
import useDepositCheckoutForm from '../../hooks/global/useDepositCheckoutForm';
import {
  isUserAdminOrManager,
  openExternalUrl,
} from '../../utils/global/helper';
import { removeFromQueue, userHasAccessToQueue } from '../../helpers/queue';
import useRemoveFromQueue from '../../hooks/queue/useRemoveFromQueue';
import { invalidateQueue, moveBackToQueue } from '../../services/queue';
import useQueueSettings from '../../hooks/queue/useQueueSettings';
import useDeliveryMethods from '../../hooks/global/useDeliveryMethods';
import useAddIDToWaiverRequest from '../../hooks/waivers/useAddIDToWaiverRequest';
import useNavigateToCustomerProfile from '../../hooks/customers/useNavigateToCustomerProfile';
import useBusinessWebRedesign from '../../hooks/businesses/useBusinessWebRedesign';
import CustomerSideBarModal from '../customers/CustomerSideBarModal';
import { cancelCheckout } from '../../services/payments';
import useGetUnreadRequests from '../../hooks/requests/useGetUnreadRequests';
import useMarkRequestAsRead from '../../hooks/requests/useMarkRequestAsRead';

type Props = {
  isOpen: boolean;
  onClose: () => void;
  onRefetchAppointments?: () => void | Promise<void>;
  onEdit?: (appointment: Appointment) => void;
  noActions?: boolean;
} & (
  | { appointment: Appointment | null; onRefresh: () => void | Promise<void> }
  | { appointmentId: string }
);

const UNCANCEL_PREVIOUS_STATUSES = ['SCHEDULED', 'CONFIRMED'];

function AppointmentSideBarModal({
  isOpen,
  onClose,
  onRefetchAppointments,
  onEdit,
  noActions,
  ...props
}: Props) {
  const { active: redesignActive } = useBusinessWebRedesign();
  const contentRef = React.useRef<HTMLDivElement>(null);
  const printAppointment = useReactToPrint({ contentRef });
  const confirm = useConfirm();
  const awaitForm = useAwaitedForm();
  const { openLoadingModal, closeLoadingModal } = useLoadingModal();
  const navigate = useNavigate();
  const piercingLocations = usePiercingLocations();
  const { user } = useUser();
  const terminalReaderId = user?.business?.terminalReaderId;
  const { sizeOptions } = useSizeOptions();
  const { openCheckout } = useCheckoutForm();
  const { getAllInPrice, cashDiscountEnabled: businessCashDiscountEnabled } =
    useCashDiscount();
  const { openDepositCheckout } = useDepositCheckoutForm();
  const { openSubscriptionModal } = useSubscriptionModal();
  const { resetWaiversPage } = useWaiverRequestsTableActions({});
  const { set: setInitialAppointmentForm } = useInitialAppointmentForm();
  const { mutateAsync: removeFromQueueFn } = useRemoveFromQueue();
  const [, setIsNewAppointmentFormOpen] = useAppointmentFormOpen();
  const queueSettings = useQueueSettings();
  const getDeliveryMethods = useDeliveryMethods();
  const { openedAppointmentIntent, setOpenedAppointmentIntent } =
    useCalendarSelectedAppointment();
  const { mutateAsync: addIDToWaiverRequest } = useAddIDToWaiverRequest({
    onError: err => {
      toast.error(err.message);
    },
    onSuccess: () => {
      toast.success('ID added to waiver request');
    },
  });

  const [isTransferDepositModalOpen, setIsTransferDepositModalOpen] =
    React.useState(false);
  const [isCustomerSideBarModalOpen, setIsCustomerSideBarModalOpen] =
    React.useState(false);
  const [isRefundDepositModalOpen, setIsRefundDepositModalOpen] =
    React.useState(false);

  const {
    data: fetchedAppointment,
    refetch: refetchAppointment,
    isPlaceholderData,
  } = useGetAppointment('appointmentId' in props ? props.appointmentId : null);

  const appointment =
    ('appointment' in props
      ? props.appointment
      : isPlaceholderData
      ? null
      : fetchedAppointment) || null;

  const { data: unreadRequests } = useGetUnreadRequests({
    enabled: isOpen,
  });
  const { mutate: markRequestAsRead } = useMarkRequestAsRead();
  const markedReadAppointmentIdRef = React.useRef<string | null>(null);

  React.useEffect(() => {
    if (!isOpen) {
      markedReadAppointmentIdRef.current = null;
      return;
    }
    const appointmentId = appointment?.appointmentId;
    if (!appointmentId) return;
    if (markedReadAppointmentIdRef.current === appointmentId) return;
    if (!unreadRequests?.scheduledAppointments?.includes(appointmentId)) return;
    markedReadAppointmentIdRef.current = appointmentId;
    markRequestAsRead({ requestId: appointmentId, type: 'appointment' });
  }, [isOpen, appointment?.appointmentId, unreadRequests, markRequestAsRead]);

  const { data: waivers } = useGetWaivers({
    businessId: appointment?.businessId,
  });

  const { payments: appointmentPayments } =
    useGetAppointmentPayments(appointment);
  const paidDepositPayment = appointmentPayments?.find(
    p => p.type === 'DEPOSIT' && p.status === 'SUCCESS',
  );
  const depositPaymentStatus = appointmentPayments?.find(
    p =>
      p.type === 'DEPOSIT' &&
      (p.status === 'SUCCESS' ||
        p.status === 'REFUNDED' ||
        p.status === 'VOIDED'),
  )?.depositStatus;

  const hasPaymentAccess =
    !!appointment &&
    !!user &&
    (appointment.employeeId === user.userId || isUserAdminOrManager(user));
  const allowRefundDeposit =
    !!paidDepositPayment &&
    !!user?.canIssueRefunds &&
    !checkPaymentType(paidDepositPayment, 'IMPORT') &&
    !!paidDepositPayment.transfers?.length &&
    hasPaymentAccess;
  const depositTransaction = paidDepositPayment
    ? convertPaymentsToTransactions([paidDepositPayment], {
        appointment,
        businessCashDiscountEnabled,
      })[0]
    : null;

  const hasTransactions =
    convertPaymentsToTransactions(appointmentPayments, {
      appointment,
      businessCashDiscountEnabled,
    }).filter(transaction => transaction.status !== 'ARCHIVED').length > 0;

  const isReadOnly =
    !!user &&
    !!appointment &&
    !isUserAdminOrManager(user) &&
    appointment.employeeId !== user.userId;

  const hasWaiversFeature = useFeatureGuard(Feature.WAIVERS);
  const canManageAppointment = useFeatureGuard(Feature.MANAGE_APPOINTMENT);
  const showWaiversFeatures = isWaiverSelectable({
    appointmentType: appointment?.appointmentType,
    serviceId: appointment?.serviceId,
    waivers,
    hasWaiversFeature,
  });

  const refresh = React.useCallback(async () => {
    if ('appointmentId' in props) {
      await refetchAppointment();
    } else if ('appointment' in props) {
      await props.onRefresh?.();
    }
  }, [props, refetchAppointment]);

  const navigateToCustomerProfile = useNavigateToCustomerProfile();

  const navigateToCustomer = React.useCallback(() => {
    if (!appointment?.customerId) return;
    if (redesignActive) {
      navigateToCustomerProfile(appointment.customerId);
      return;
    }
    setIsCustomerSideBarModalOpen(true);
  }, [appointment?.customerId, redesignActive, navigateToCustomerProfile]);

  const sendConfirmReminder = React.useCallback(async () => {
    // If the appointment is not an appointment, return
    if (
      !canManageAppointment ||
      !appointment ||
      !('status' in appointment) ||
      appointment.status !== 'SCHEDULED' ||
      !user
    )
      return;

    try {
      const payload = await getDeliveryMethods({
        customer: appointment.customer,
        title: 'Confirmation reminder',
      });
      if (!payload.mediums.length) return;

      await sendAppointmentReminder(appointment, payload);
      toast.success('Reminder sent');
    } catch (e) {
      toast.error('Error sending reminder');
    }
  }, [canManageAppointment, appointment, user, getDeliveryMethods]);

  const requestAppointmentWaiver = React.useCallback(async () => {
    if (
      !canManageAppointment ||
      !appointment ||
      !('status' in appointment) ||
      !user ||
      !showWaiversFeatures
    )
      return;
    try {
      await requestWaiver({
        appointment,
      });
      resetWaiversPage();
      await refresh();
      toast.success('Waiver requested');
    } catch (e) {
      toast.error('Error requesting waiver');
    }
  }, [
    canManageAppointment,
    appointment,
    user,
    showWaiversFeatures,
    resetWaiversPage,
    refresh,
  ]);

  const resendWaiverReminder = React.useCallback(async () => {
    if (
      !canManageAppointment ||
      !appointment ||
      !('status' in appointment) ||
      !appointment.businessId ||
      !appointment.waiverRequest ||
      !user
    )
      return;

    try {
      const payload = await getDeliveryMethods({
        customer: appointment.customer,
        title: 'Waiver reminder',
      });
      if (!payload.mediums.length) return;

      await sendWaiverReminder({
        businessId: appointment.businessId,
        waiverRequestId: appointment.waiverRequest.id,
        ...payload,
      });
      toast.success('Waiver link resent');
    } catch (e) {
      toast.error('Error sending waiver link');
    }
  }, [canManageAppointment, user, appointment, getDeliveryMethods]);

  const completeWaiverOnDevice = React.useCallback(async () => {
    if (
      !canManageAppointment ||
      !appointment ||
      !('status' in appointment) ||
      !appointment.businessId ||
      !appointment.waiverRequest ||
      !user
    )
      return;

    try {
      const { session, business } = await createWaiverRequestSession({
        businessId: appointment.businessId,
        waiverRequestId: appointment.waiverRequest.id,
      });
      if (!session?.id) return;
      const url = getBusinessShopUrl(business, `/waiver-session/${session.id}`);
      openExternalUrl(url);
    } catch (e) {
      toast.error('Error completing waiver');
    }
  }, [canManageAppointment, user, appointment]);

  const markAsNoShow = React.useCallback(async () => {
    if (!canManageAppointment || !appointment || !('status' in appointment))
      return;

    try {
      const markingNoShow = !appointment.wasNoShow;
      await markAppointmentNoShow(appointment.appointmentId, markingNoShow);
      if (markingNoShow) {
        await invalidateDepositTransferAppointments();
      }
      await refresh();
      toast.success(
        appointment.wasNoShow
          ? 'No-show undone'
          : 'Appointment marked as no-show',
      );
    } catch (e) {
      toast.error('Error marking appointment as no-show');
    }
  }, [canManageAppointment, appointment, refresh]);

  const reactivateWaiver = React.useCallback(async () => {
    if (
      !canManageAppointment ||
      !appointment ||
      !('status' in appointment) ||
      !appointment.businessId ||
      !user ||
      appointment.waiverRequest?.status !== 'CANCELED' ||
      !showWaiversFeatures
    )
      return;

    try {
      await reactivateWaiverRequest({
        businessId: appointment.businessId,
        waiverRequestId: appointment.waiverRequest.id,
      });
      resetWaiversPage();
      await refresh();
      toast.success('Waiver reactivated');
    } catch (e) {
      toast.error('Error reactivating waiver');
    }
  }, [
    canManageAppointment,
    appointment,
    user,
    showWaiversFeatures,
    resetWaiversPage,
    refresh,
  ]);

  const scheduleNextAppointment = React.useCallback(async () => {
    if (!canManageAppointment || !appointment) return;
    setInitialAppointmentForm(getAppointmentFormValues(appointment, user));
    setIsNewAppointmentFormOpen(true);
    onClose();
  }, [
    canManageAppointment,
    appointment,
    setInitialAppointmentForm,
    setIsNewAppointmentFormOpen,
    onClose,
    user,
  ]);

  const uncancelAppointmentAction = React.useCallback(async () => {
    if (
      !appointment ||
      !appointment.startDateTime ||
      appointment.status !== 'CANCELED' ||
      !appointment.previousStatus ||
      !UNCANCEL_PREVIOUS_STATUSES.includes(appointment.previousStatus) ||
      !isFuture(new Date(appointment.startDateTime))
    )
      return;

    try {
      openLoadingModal({
        header: 'Uncanceling Appointment',
        message: 'Please wait while we schedule the appointment...',
      });
      await uncancelAppointment(appointment.appointmentId);
      await refresh();
      toast.success('Appointment uncanceled');
    } catch (e) {
      if (isAxiosError(e) && e.response?.data?.code === 'BOOKING-105') {
        toast.error(e.response?.data.message);
      } else {
        toast.error('Error uncanceling appointment');
      }
    } finally {
      closeLoadingModal();
    }
  }, [appointment, refresh, openLoadingModal, closeLoadingModal]);

  const transferDeposit = React.useCallback(async () => {
    setIsTransferDepositModalOpen(true);
  }, []);

  const moveBackToQueueAction = React.useCallback(async () => {
    if (!canManageAppointment || !appointment) return;
    try {
      await moveBackToQueue(appointment.appointmentId);
      await refresh();
      await invalidateQueue();
      toast.success('Appointment moved back to queue');
    } catch (e) {
      toast.error('Error moving appointment back to queue');
    }
  }, [canManageAppointment, appointment, refresh]);

  const onAddIDToWaiverRequest = React.useCallback(async () => {
    if (!appointment?.waiverRequest?.id) return;
    await addIDToWaiverRequest(appointment?.waiverRequest?.id);
    refresh();
  }, [appointment?.waiverRequest?.id, addIDToWaiverRequest, refresh]);

  const isEmployeeInactiveAtBusiness = React.useMemo(() => {
    if (!appointment?.employeeId) return false;
    if (!user?.business?.id) return false;
    if (appointment.businessId !== user.business.id) return false;
    return !user.business.employees?.some(
      e => e.employeeId === appointment.employeeId,
    );
  }, [appointment, user]);

  const actions = React.useMemo(() => {
    const moreActions: FooterAction[] = [];
    if (!appointment || noActions || isReadOnly) return { moreActions: [] };
    if (isEmployeeInactiveAtBusiness) return { moreActions: [] };

    const isScheduledOrConfirmed =
      appointment.status === 'SCHEDULED' || appointment.status === 'CONFIRMED';

    const scheduleNextAction = createFooterAction(
      'Schedule next appointment',
      canManageAppointment ? scheduleNextAppointment : openSubscriptionModal,
    );

    if (
      !!appointment &&
      'status' in appointment &&
      appointment.status !== 'CANCELED' &&
      appointment.status !== 'ARCHIVED'
    ) {
      if (
        appointment.status === 'SCHEDULED' &&
        !appointment.checkoutSessionActive &&
        !appointment.checkoutSessionCompleted &&
        appointment.appointmentType !== 'WALK_IN' &&
        appointment.appointmentType !== 'CONSULTATION'
      ) {
        moreActions.push(
          createFooterAction(
            'Send reminder to confirm',
            canManageAppointment ? sendConfirmReminder : openSubscriptionModal,
          ),
        );
      }
      const isDepositPaid = isAppointmentDepositPaid(appointment);
      if (
        !isDepositPaid &&
        (appointment.depositPrice ?? 0) > 0 &&
        !appointment.checkoutSessionActive &&
        !appointment.checkoutSessionCompleted
      ) {
        moreActions.push(
          createFooterAction(
            'Checkout deposit',
            canManageAppointment
              ? () => openDepositCheckout(appointment.appointmentId, refresh)
              : openSubscriptionModal,
          ),
        );
      }

      const now = new Date();
      const startDate = appointment.startDateTime
        ? new Date(appointment.startDateTime)
        : null;
      // still allow same day after now
      const isValidDay =
        !!startDate && (isSameDay(now, startDate) || isBefore(now, startDate));

      if (
        !appointment?.waiverRequest &&
        appointment?.businessId &&
        isValidDay &&
        showWaiversFeatures &&
        appointment.appointmentType !== 'CONSULTATION'
      ) {
        moreActions.push(
          createFooterAction(
            'Request waiver',
            canManageAppointment
              ? requestAppointmentWaiver
              : openSubscriptionModal,
          ),
        );
      }

      const isCurrentDay = !!startDate && isSameDay(now, startDate);
      if (
        appointment?.waiverRequest &&
        appointment?.businessId &&
        isCurrentDay &&
        appointment.waiverRequest.status !== 'SIGNED' &&
        appointment.waiverRequest.status !== 'CANCELED'
      ) {
        moreActions.push(
          createFooterAction(
            'Resend waiver link',
            canManageAppointment ? resendWaiverReminder : openSubscriptionModal,
          ),
          createFooterAction(
            'Complete waiver on device',
            canManageAppointment
              ? completeWaiverOnDevice
              : openSubscriptionModal,
          ),
        );
      }

      if (
        appointment?.waiverRequest?.status === 'SIGNED' &&
        appointment?.waiverRequest?.signedFileLink
      ) {
        moreActions.push(
          createFooterAction(
            'Add ID file to waiver',
            canManageAppointment
              ? onAddIDToWaiverRequest
              : openSubscriptionModal,
          ),
        );
      }

      if (
        appointment?.waiverRequest?.status === 'CANCELED' &&
        isValidDay &&
        showWaiversFeatures
      ) {
        moreActions.push(
          createFooterAction(
            'Reactivate waiver',
            canManageAppointment ? reactivateWaiver : openSubscriptionModal,
          ),
        );
      }

      if (appointment?.depositAgreementRequest?.signedFileLink) {
        moreActions.push(
          createFooterAction('View deposit agreement', () =>
            openStorageUrl(
              appointment.depositAgreementRequest!.signedFileLink!,
            ),
          ),
        );
      }

      if (
        isScheduledOrConfirmed &&
        !appointment.checkoutSessionActive &&
        !appointment.checkoutSessionCompleted &&
        appointment.startDateTime
      ) {
        moreActions.push(
          createFooterAction(
            appointment.wasNoShow ? 'Undo no-show' : 'Mark as no-show',
            canManageAppointment ? markAsNoShow : openSubscriptionModal,
          ),
        );

        if (
          !isDepositPaid &&
          ((!appointment.businessId &&
            appointment.employeeId === user?.userId) ||
            appointment.businessId === user?.business?.id)
        ) {
          moreActions.push(
            createFooterAction(
              'Apply a deposit from clients profile',
              canManageAppointment ? transferDeposit : openSubscriptionModal,
            ),
          );
        }

        if (
          !!user &&
          appointment.appointmentType === 'WALK_IN' &&
          appointment.businessId === user?.business?.id &&
          queueSettings.enabled &&
          userHasAccessToQueue(user)
        ) {
          moreActions.push(
            createFooterAction(
              'Move to walk-in queue',
              canManageAppointment
                ? moveBackToQueueAction
                : openSubscriptionModal,
            ),
          );
        }
      }

      if (isScheduledOrConfirmed && appointment.startDateTime) {
        moreActions.push(scheduleNextAction);
      }

      if (appointment.status === 'QUEUED') {
        if (onEdit) {
          moreActions.push(
            createFooterAction(
              'Assign to artist',
              canManageAppointment
                ? () => onEdit(appointment)
                : openSubscriptionModal,
            ),
          );
        }

        moreActions.push(
          createFooterAction(
            'Remove from queue',
            canManageAppointment
              ? () =>
                  removeFromQueue({
                    appointment,
                    asyncForm: awaitForm,
                    loadingModal: {
                      open: openLoadingModal,
                      close: closeLoadingModal,
                    },
                    user: user!,
                    removeFromQueue: removeFromQueueFn,
                  })
              : openSubscriptionModal,
          ),
        );
      }
    } else if (appointment.status === 'CANCELED' && appointment.startDateTime) {
      moreActions.push(scheduleNextAction);

      if (
        isFuture(new Date(appointment.startDateTime)) &&
        appointment.previousStatus &&
        UNCANCEL_PREVIOUS_STATUSES.includes(appointment.previousStatus)
      ) {
        moreActions.push(
          createFooterAction(
            'Uncancel appointment',
            canManageAppointment
              ? uncancelAppointmentAction
              : openSubscriptionModal,
          ),
        );
      }
    }

    return {
      moreActions,
    };
  }, [
    appointment,
    noActions,
    canManageAppointment,
    refresh,
    scheduleNextAppointment,
    openSubscriptionModal,
    showWaiversFeatures,
    sendConfirmReminder,
    openDepositCheckout,
    requestAppointmentWaiver,
    resendWaiverReminder,
    completeWaiverOnDevice,
    onAddIDToWaiverRequest,
    reactivateWaiver,
    markAsNoShow,
    user,
    queueSettings.enabled,
    transferDeposit,
    moveBackToQueueAction,
    onEdit,
    awaitForm,
    openLoadingModal,
    closeLoadingModal,
    removeFromQueueFn,
    uncancelAppointmentAction,
    isReadOnly,
    isEmployeeInactiveAtBusiness,
  ]);

  const openChat = React.useCallback(async () => {
    if (!canManageAppointment) return;
    if (appointment && user) {
      try {
        openLoadingModal({
          header: 'Opening chat',
          message: 'Please wait while we load the chat...',
        });
        const chatResponse = await createAppointmentChat(appointment, user);
        navigateToChat(navigate, chatResponse);
      } catch {
        // Empty
      } finally {
        closeLoadingModal();
      }
    }
  }, [
    canManageAppointment,
    appointment,
    user,
    openLoadingModal,
    navigate,
    closeLoadingModal,
  ]);

  const cancelAppointmentAction = React.useCallback(async () => {
    if (!canManageAppointment || !appointment || !appointment.employee) return;

    let depositStatus: DepositPaymentStatus | undefined;

    const isDepositPaid =
      isAppointmentDepositPaid(appointment) && !appointment.isDepositRefunded;
    if (isDepositPaid) {
      const formResult = await awaitForm<AppointmentRefundDepositState>({
        form: {
          title: 'Cancel Appointment',
          submitButtonText: 'Cancel Appointment',
          inputRows: [
            [
              {
                inputType: 'select',
                name: 'depositStatus',
                label:
                  'What do you want to do with the deposit on this appointment?',
                placeholder: 'Select an option',
                isLabelMultiline: true,
                options: getCancelAppointmentDepositOptions(appointment),
                autoWidth: false,
              },
            ],
          ],
          initialValues: {
            depositStatus: '' as DepositPaymentStatus,
          },
          validationSchema: appointmentRefundDepositSchema,
        },
      });
      if (!formResult) return;

      ({ depositStatus } = formResult);
    }

    const confirmed = await confirm({
      title: 'Cancel Appointment',
      message: `Are you sure you want to cancel ${appointment?.employee
        .displayName}'s appointment with ${appointment?.customer
        .firstName} ${appointment?.customer.lastName}${
        appointment?.startDateTime
          ? ` scheduled for ${format(
              new Date(appointment.startDateTime),
              'MM/dd/yyyy',
            )}?`
          : '?'
      }. This action cannot be undone.`,
      negativeText: '',
      positiveText: 'Cancel Appointment',
    });
    if (!confirmed) return;

    try {
      openLoadingModal({
        header: 'Canceling Appointment',
        message:
          'Please wait while we cancel the appointment. This may take a few moments.',
      });

      const cancelResponse = await cancelAppointment(appointment, {
        depositStatus,
      });

      if (cancelResponse) {
        await refresh();
        toast.success('Appointment canceled!');
        await onRefetchAppointments?.();

        if (depositStatus) {
          await invalidateAppointmentPayments(appointment.appointmentId);
        }
      }
    } catch (cancelError) {
      toast.error('Error canceling appointment');
    } finally {
      closeLoadingModal();
    }
  }, [
    canManageAppointment,
    appointment,
    awaitForm,
    confirm,
    openLoadingModal,
    refresh,
    onRefetchAppointments,
    closeLoadingModal,
  ]);

  const footerActions = React.useMemo(() => {
    if (!appointment || noActions || isReadOnly) return [];
    if (isEmployeeInactiveAtBusiness) {
      if (!onEdit) return [];
      return [
        createFooterAction(
          'Edit',
          !canManageAppointment
            ? openSubscriptionModal
            : () => onEdit(appointment),
        ),
      ];
    }

    // Canceled and archived appointments keep chat but nothing editable
    const isEditable =
      appointment.status !== 'CANCELED' && appointment.status !== 'ARCHIVED';
    const isCancelable =
      isEditable &&
      !appointment.checkoutSessionCompleted &&
      !appointment.wasNoShow;

    const tempActions: FooterAction[] = [];
    if (onEdit && isEditable) {
      tempActions.push(
        createFooterAction(
          'Edit',
          !canManageAppointment
            ? openSubscriptionModal
            : () => onEdit(appointment),
        ),
      );
    }
    if (isCancelable) {
      tempActions.push(
        createFooterAction(
          'Cancel',
          !canManageAppointment
            ? openSubscriptionModal
            : () => cancelAppointmentAction(),
        ),
      );
    }
    tempActions.push(
      createFooterAction(
        'Open Chat',
        !canManageAppointment ? openSubscriptionModal : openChat,
      ),
    );
    return tempActions;
  }, [
    appointment,
    canManageAppointment,
    openSubscriptionModal,
    openChat,
    onEdit,
    cancelAppointmentAction,
    noActions,
    isReadOnly,
    isEmployeeInactiveAtBusiness,
  ]);

  const [isResendingCheckoutLink, setIsResendingCheckoutLink] =
    React.useState(false);

  const resendCheckoutLinkAction = React.useCallback(async () => {
    if (
      !canManageAppointment ||
      !appointment ||
      !appointment.checkoutSessionActive ||
      appointment.checkoutSessionCompleted ||
      appointment.paymentMethod !== 'CREDIT' ||
      isResendingCheckoutLink
    )
      return;

    try {
      setIsResendingCheckoutLink(true);
      const payload = await getDeliveryMethods({
        customer: appointment.customer,
        title: 'Resend checkout link',
      });
      if (!payload.mediums.length) return;

      await resendCheckoutLink(appointment.appointmentId, payload);
      toast.success('Checkout link re-sent');
    } catch (e) {
      toast.error('Error re-sending checkout link');
    } finally {
      setIsResendingCheckoutLink(false);
    }
  }, [
    canManageAppointment,
    appointment,
    getDeliveryMethods,
    isResendingCheckoutLink,
  ]);

  const [isCancelingTerminalCheckout, setIsCancelingTerminalCheckout] =
    React.useState(false);

  const cancelTerminalCheckoutAction = React.useCallback(async () => {
    if (
      !canManageAppointment ||
      !appointment ||
      !appointment.checkoutSessionActive ||
      appointment.checkoutSessionCompleted ||
      appointment.paymentMethod !== 'TERMINAL' ||
      isCancelingTerminalCheckout
    )
      return;

    try {
      setIsCancelingTerminalCheckout(true);
      await cancelCheckout({
        appointmentId: appointment.appointmentId,
        terminalReaderId,
      });
      toast.success('Terminal checkout canceled.');
      await refresh();
    } catch (e) {
      toast.error('Error canceling terminal checkout');
    } finally {
      setIsCancelingTerminalCheckout(false);
    }
  }, [
    canManageAppointment,
    appointment,
    terminalReaderId,
    isCancelingTerminalCheckout,
    refresh,
  ]);

  const uncancelEligible = React.useMemo(
    () =>
      !!appointment &&
      appointment.status === 'CANCELED' &&
      !!appointment.startDateTime &&
      isFuture(new Date(appointment.startDateTime)) &&
      !!appointment.previousStatus &&
      UNCANCEL_PREVIOUS_STATUSES.includes(appointment.previousStatus),
    [appointment],
  );

  const primaryAction: FooterAction | null = React.useMemo(() => {
    if (!appointment || noActions || isReadOnly || isEmployeeInactiveAtBusiness)
      return null;

    if (
      appointment.checkoutSessionActive &&
      !appointment.checkoutSessionCompleted
    ) {
      if (appointment.paymentMethod === 'TERMINAL') {
        return createFooterAction(
          'Cancel Checkout',
          canManageAppointment
            ? cancelTerminalCheckoutAction
            : openSubscriptionModal,
        );
      }
      if (appointment.paymentMethod !== 'CREDIT') return null;
      return createFooterAction(
        'Resend checkout link',
        canManageAppointment ? resendCheckoutLinkAction : openSubscriptionModal,
      );
    }

    if (appointment.checkoutSessionCompleted || appointment.wasNoShow) {
      if (!appointment.startDateTime) return null;
      return createFooterAction(
        'Schedule next appointment',
        canManageAppointment ? scheduleNextAppointment : openSubscriptionModal,
      );
    }

    if (appointment.status === 'CONFIRMED') {
      return createFooterAction(
        'Review & Checkout',
        canManageAppointment
          ? () => openCheckout(appointment)
          : openSubscriptionModal,
      );
    }

    if (appointment.status === 'SCHEDULED') {
      const startDate = appointment.startDateTime
        ? new Date(appointment.startDateTime)
        : null;
      const isDayOf = !!startDate && isSameDay(new Date(), startDate);

      if (isDayOf) {
        return createFooterAction(
          'Review & Checkout',
          canManageAppointment
            ? () => openCheckout(appointment)
            : openSubscriptionModal,
        );
      }

      if (
        appointment.appointmentType !== 'WALK_IN' &&
        appointment.appointmentType !== 'CONSULTATION'
      ) {
        return createFooterAction(
          'Send reminder to confirm',
          canManageAppointment ? sendConfirmReminder : openSubscriptionModal,
        );
      }

      return null;
    }

    if (appointment.status === 'CANCELED') {
      if (uncancelEligible) {
        return createFooterAction(
          'Uncancel appointment',
          canManageAppointment
            ? uncancelAppointmentAction
            : openSubscriptionModal,
        );
      }
      if (appointment.startDateTime) {
        return createFooterAction(
          'Schedule next appointment',
          canManageAppointment
            ? scheduleNextAppointment
            : openSubscriptionModal,
        );
      }
      return null;
    }

    if (appointment.status === 'QUEUED') {
      if (!onEdit) return null;
      return createFooterAction(
        'Assign to artist',
        canManageAppointment
          ? () => onEdit(appointment)
          : openSubscriptionModal,
      );
    }

    return null;
  }, [
    appointment,
    noActions,
    isReadOnly,
    isEmployeeInactiveAtBusiness,
    canManageAppointment,
    resendCheckoutLinkAction,
    cancelTerminalCheckoutAction,
    openSubscriptionModal,
    scheduleNextAppointment,
    openCheckout,
    sendConfirmReminder,
    uncancelEligible,
    uncancelAppointmentAction,
    onEdit,
  ]);

  const scheduleNextIsPrimary =
    !!appointment &&
    (!!appointment.checkoutSessionCompleted ||
      !!appointment.wasNoShow ||
      (appointment.status === 'CANCELED' && !uncancelEligible));
  const confirmReminderIsPrimary =
    primaryAction?.label === 'Send reminder to confirm';

  const kebabActionGroups: FooterActionGroup[] = React.useMemo(() => {
    if (!appointment || noActions || isReadOnly || isEmployeeInactiveAtBusiness)
      return [];

    const notArchived = appointment.status !== 'ARCHIVED';
    const isScheduledOrConfirmed =
      appointment.status === 'SCHEDULED' || appointment.status === 'CONFIRMED';
    const isDepositPaid = isAppointmentDepositPaid(appointment);

    const now = new Date();
    const startDate = appointment.startDateTime
      ? new Date(appointment.startDateTime)
      : null;
    const isValidDay =
      !!startDate && (isSameDay(now, startDate) || isBefore(now, startDate));
    const isCurrentDay = !!startDate && isSameDay(now, startDate);

    const scheduling: FooterAction[] = [];
    const notifications: FooterAction[] = [
      createFooterAction(
        'Open chat',
        canManageAppointment ? openChat : openSubscriptionModal,
      ),
    ];
    const statusActions: FooterAction[] = [];
    const history: FooterAction[] = [];
    const danger: FooterAction[] = [];

    // Day-of, the primary slot becomes Review & Checkout
    if (
      !confirmReminderIsPrimary &&
      appointment.status === 'SCHEDULED' &&
      !appointment.checkoutSessionActive &&
      !appointment.checkoutSessionCompleted &&
      appointment.appointmentType !== 'WALK_IN' &&
      appointment.appointmentType !== 'CONSULTATION'
    ) {
      notifications.push(
        createFooterAction(
          'Send reminder to confirm',
          canManageAppointment ? sendConfirmReminder : openSubscriptionModal,
        ),
      );
    }

    if (
      notArchived &&
      !scheduleNextIsPrimary &&
      ((isScheduledOrConfirmed && appointment.startDateTime) ||
        (appointment.status === 'CANCELED' && appointment.startDateTime))
    ) {
      scheduling.push(
        createFooterAction(
          'Schedule next appointment',
          canManageAppointment
            ? scheduleNextAppointment
            : openSubscriptionModal,
        ),
      );
    }

    if (appointment.status === 'QUEUED') {
      scheduling.push(
        createFooterAction(
          'Remove from queue',
          canManageAppointment
            ? () =>
                removeFromQueue({
                  appointment,
                  asyncForm: awaitForm,
                  loadingModal: {
                    open: openLoadingModal,
                    close: closeLoadingModal,
                  },
                  user: user!,
                  removeFromQueue: removeFromQueueFn,
                })
            : openSubscriptionModal,
        ),
      );
    }

    if (
      notArchived &&
      isScheduledOrConfirmed &&
      !appointment.checkoutSessionActive &&
      !appointment.checkoutSessionCompleted &&
      appointment.startDateTime &&
      !!user &&
      appointment.appointmentType === 'WALK_IN' &&
      appointment.businessId === user?.business?.id &&
      queueSettings.enabled &&
      userHasAccessToQueue(user)
    ) {
      scheduling.push(
        createFooterAction(
          'Move to walk-in queue',
          canManageAppointment ? moveBackToQueueAction : openSubscriptionModal,
        ),
      );
    }

    if (
      notArchived &&
      appointment.waiverRequest &&
      appointment.businessId &&
      isCurrentDay &&
      appointment.waiverRequest.status !== 'SIGNED' &&
      appointment.waiverRequest.status !== 'CANCELED'
    ) {
      notifications.push(
        createFooterAction(
          'Resend waiver link',
          canManageAppointment ? resendWaiverReminder : openSubscriptionModal,
        ),
      );
    }

    if (
      notArchived &&
      appointment.status !== 'CANCELED' &&
      !isDepositPaid &&
      (appointment.depositPrice ?? 0) > 0 &&
      !appointment.checkoutSessionActive &&
      !appointment.checkoutSessionCompleted
    ) {
      statusActions.push(
        createFooterAction(
          'Checkout deposit',
          canManageAppointment
            ? () => openDepositCheckout(appointment.appointmentId, refresh)
            : openSubscriptionModal,
        ),
      );
    }

    if (
      notArchived &&
      appointment.status !== 'CANCELED' &&
      !appointment.waiverRequest &&
      appointment.businessId &&
      isValidDay &&
      showWaiversFeatures &&
      appointment.appointmentType !== 'CONSULTATION'
    ) {
      statusActions.push(
        createFooterAction(
          'Request waiver',
          canManageAppointment
            ? requestAppointmentWaiver
            : openSubscriptionModal,
        ),
      );
    }

    if (
      notArchived &&
      appointment.waiverRequest &&
      appointment.businessId &&
      isCurrentDay &&
      appointment.waiverRequest.status !== 'SIGNED' &&
      appointment.waiverRequest.status !== 'CANCELED'
    ) {
      statusActions.push(
        createFooterAction(
          'Complete waiver on device',
          canManageAppointment ? completeWaiverOnDevice : openSubscriptionModal,
        ),
      );
    }

    if (
      notArchived &&
      appointment.waiverRequest?.status === 'SIGNED' &&
      appointment.waiverRequest?.signedFileLink
    ) {
      statusActions.push(
        createFooterAction(
          'Add ID file to waiver',
          canManageAppointment ? onAddIDToWaiverRequest : openSubscriptionModal,
        ),
      );
    }

    if (
      notArchived &&
      appointment.waiverRequest?.status === 'CANCELED' &&
      isValidDay &&
      showWaiversFeatures
    ) {
      statusActions.push(
        createFooterAction(
          'Reactivate waiver',
          canManageAppointment ? reactivateWaiver : openSubscriptionModal,
        ),
      );
    }

    if (
      isScheduledOrConfirmed &&
      !appointment.checkoutSessionActive &&
      !appointment.checkoutSessionCompleted &&
      appointment.startDateTime
    ) {
      statusActions.push(
        createFooterAction(
          appointment.wasNoShow ? 'Undo no-show' : 'Mark as no-show',
          canManageAppointment ? markAsNoShow : openSubscriptionModal,
        ),
      );

      if (
        !isDepositPaid &&
        ((!appointment.businessId && appointment.employeeId === user?.userId) ||
          appointment.businessId === user?.business?.id)
      ) {
        statusActions.push(
          createFooterAction(
            'Apply a deposit from clients profile',
            canManageAppointment ? transferDeposit : openSubscriptionModal,
          ),
        );
      }
    }

    if (notArchived && appointment.depositAgreementRequest?.signedFileLink) {
      history.push(
        createFooterAction('View deposit agreement', () =>
          openStorageUrl(appointment.depositAgreementRequest!.signedFileLink!),
        ),
      );
    }

    if (
      notArchived &&
      appointment.waiverRequest?.status === 'SIGNED' &&
      appointment.waiverRequest?.signedFileLink &&
      getWaiverRequestStatus({
        appointment,
        waiverRequest: appointment.waiverRequest,
        waivers,
      }) !== 'pendingEmployee'
    ) {
      history.push(
        createFooterAction('View signed waiver', () =>
          openStorageUrl(appointment.waiverRequest!.signedFileLink!),
        ),
      );
    }

    history.push(createFooterAction('Print details', printAppointment));

    if (
      notArchived &&
      appointment.status !== 'CANCELED' &&
      !appointment.checkoutSessionCompleted &&
      !appointment.wasNoShow
    ) {
      danger.push(
        createFooterAction(
          'Cancel',
          !canManageAppointment
            ? openSubscriptionModal
            : () => cancelAppointmentAction(),
        ),
      );
    }

    if (allowRefundDeposit) {
      danger.push(
        createFooterAction('Refund deposit', () =>
          setIsRefundDepositModalOpen(true),
        ),
      );
    }

    return [
      { group: 'Scheduling', actions: scheduling },
      { group: 'Notifications', actions: notifications },
      { group: 'Status', actions: statusActions },
      { group: 'History', actions: history },
      { group: 'Danger', actions: danger },
    ];
  }, [
    appointment,
    noActions,
    isReadOnly,
    isEmployeeInactiveAtBusiness,
    scheduleNextIsPrimary,
    confirmReminderIsPrimary,
    sendConfirmReminder,
    canManageAppointment,
    scheduleNextAppointment,
    openSubscriptionModal,
    awaitForm,
    openLoadingModal,
    closeLoadingModal,
    user,
    removeFromQueueFn,
    queueSettings.enabled,
    moveBackToQueueAction,
    resendWaiverReminder,
    showWaiversFeatures,
    requestAppointmentWaiver,
    completeWaiverOnDevice,
    onAddIDToWaiverRequest,
    reactivateWaiver,
    openDepositCheckout,
    refresh,
    markAsNoShow,
    transferDeposit,
    cancelAppointmentAction,
    openChat,
    waivers,
    printAppointment,
    allowRefundDeposit,
  ]);

  const showEditRedesign =
    !!appointment &&
    !!onEdit &&
    !noActions &&
    !isReadOnly &&
    appointment.status !== 'CANCELED' &&
    appointment.status !== 'ARCHIVED';

  const showChatRedesign =
    !!appointment && !noActions && !isReadOnly && !isEmployeeInactiveAtBusiness;

  const onWaiverPress = React.useCallback(async () => {
    if (!appointment) return;
    const { waiverRequest } = appointment;
    if (!waiverRequest) return;

    const waiverStatus = getWaiverRequestStatus({
      appointment,
      waiverRequest,
      waivers,
    });

    const isPendingEmployee = waiverStatus === 'pendingEmployee';
    const isSent = waiverRequest.status === 'SENT';
    const isQueued = waiverRequest.status === 'QUEUED';
    const isComplete = waiverRequest.status === 'SIGNED';
    const isCanceled = waiverRequest.status === 'CANCELED';
    const willNeverComplete =
      appointment.status === 'CANCELED' && (isQueued || isSent);

    if (!isPendingEmployee && isComplete && waiverRequest.signedFileLink) {
      openStorageUrl(waiverRequest.signedFileLink);
      return;
    }

    const hourBeforeAppt = appointment.startDateTime
      ? subHours(new Date(appointment.startDateTime), 1)
      : null;
    const queuedTime = hourBeforeAppt
      ? format(new Date(hourBeforeAppt), 'h:mma')
      : null;
    const queuedDate = hourBeforeAppt
      ? format(new Date(hourBeforeAppt), 'EEEE, MMMM d')
      : null;

    const message = isPendingEmployee
      ? 'Customer has filled in the waiver, but the service provider still needs to finalize the waiver'
      : willNeverComplete
      ? 'Waiver was not completed by the customer'
      : isSent
      ? 'Waiver has been sent to customer for signature'
      : isQueued && queuedTime && queuedDate
      ? `Waiver will be sent to the customer for their signature 1 hour before the appointment at ${queuedTime} on ${queuedDate}`
      : isQueued
      ? 'Waiver will be sent to the customer for their signature'
      : isComplete
      ? 'Waiver has been completed by the customer'
      : isCanceled
      ? 'Waiver has been canceled'
      : 'Waiver has not yet been completed by the customer';

    const positiveText = isPendingEmployee ? 'Finalize Waiver' : 'Done';
    const negativeText = isPendingEmployee ? 'Back' : '';

    const confirmed = await confirm({
      dismissible: true,
      title: 'Waiver',
      message,
      positiveText,
      negativeText,
      isSmall: true,
      hideCloseButton: true,
    });

    if (confirmed && isPendingEmployee && waiverRequest.signedFileLink) {
      navigate(`${ROUTES.waiverRequest}/${waiverRequest.id}`);
    }
  }, [confirm, appointment, waivers, navigate]);

  const handledWaiverIntentRef = React.useRef(false);
  React.useEffect(() => {
    if (openedAppointmentIntent !== 'waiver') {
      handledWaiverIntentRef.current = false;
      return;
    }
    if (!appointment || handledWaiverIntentRef.current) return;
    handledWaiverIntentRef.current = true;
    setOpenedAppointmentIntent(null);
    if (appointment.waiverRequest) onWaiverPress();
  }, [
    openedAppointmentIntent,
    setOpenedAppointmentIntent,
    appointment,
    onWaiverPress,
  ]);

  const getAllInDepositAmount = React.useCallback(
    (a: Appointment) => {
      // Paid: lock to actual base + fee from the payment. This works for
      // both CD payments and legacy non-CD payments now viewed under a
      // CD-on business (FEE line item captures whatever fee was charged).
      // Non-CD business stays on depositPrice — fee is a separate line item.
      if (paidDepositPayment) {
        return paidDepositPayment.cashDiscountEnabled ||
          businessCashDiscountEnabled
          ? getDepositPaidExclTaxAmount(paidDepositPayment)
          : a.depositPrice ?? 0;
      }
      return getAllInPrice(a.depositPrice ?? 0, {
        paymentMethod: a.depositPaymentMethod ?? 'CREDIT',
      });
    },
    [getAllInPrice, paidDepositPayment, businessCashDiscountEnabled],
  );

  const modalData = React.useMemo(
    () =>
      appointment
        ? createModalData({
            appointment,
            loadedUser: user!,
            navigateToCustomer,
            openCheckoutModal: () => openCheckout(appointment),
            sizeOptions,
            piercingLocations,
            refreshModal: refresh,
            waiverButtonAction: onWaiverPress,
            waivers,
            getAllInDepositAmount,
            redesignActive,
            onOpenChat: canManageAppointment ? openChat : openSubscriptionModal,
            showChat: showChatRedesign,
            hasTransactions,
          })
        : null,
    [
      appointment,
      user,
      navigateToCustomer,
      openCheckout,
      sizeOptions,
      piercingLocations,
      refresh,
      onWaiverPress,
      waivers,
      redesignActive,
      openChat,
      showChatRedesign,
      hasTransactions,
      getAllInDepositAmount,
      canManageAppointment,
      openSubscriptionModal,
    ],
  );

  if (!('appointmentId' in props) && !appointment) return null;

  return (
    <>
      <SideBarModal
        ref={contentRef}
        name={modalData?.name || 'Appointment Detail'}
        modalOpen={isOpen}
        sections={modalData?.sections || []}
        isLoading={!modalData}
        setModalOpen={onClose}
        moreActions={modalData && !redesignActive ? actions.moreActions : []}
        footerActions={modalData && !redesignActive ? footerActions : []}
        redesignActive={redesignActive}
        backdropColor={redesignActive ? 'rgba(0,0,0,0.18)' : undefined}
        topContent={
          modalData && redesignActive && appointment ? (
            <AppointmentSidebarStatusHeader
              appointment={appointment}
              primaryAction={primaryAction}
              primaryActionLoading={
                (primaryAction?.label === 'Resend checkout link' &&
                  isResendingCheckoutLink) ||
                (primaryAction?.label === 'Cancel Checkout' &&
                  isCancelingTerminalCheckout)
              }
              kebabGroups={kebabActionGroups}
              showEdit={showEditRedesign}
              onEdit={
                onEdit
                  ? canManageAppointment
                    ? () => onEdit(appointment)
                    : openSubscriptionModal
                  : undefined
              }
              onClose={() => onClose()}
              waivers={waivers}
              depositStatus={depositPaymentStatus}
              onWaiverPress={onWaiverPress}
            />
          ) : undefined
        }
      />

      {isTransferDepositModalOpen && !!appointment && (
        <TransferAppointmentDepositModal
          isOpen
          onClose={() => setIsTransferDepositModalOpen(false)}
          onRefresh={refresh}
          appointment={appointment}
        />
      )}

      {isOpen && isCustomerSideBarModalOpen && !!appointment && (
        <CustomerSideBarModal
          customerId={appointment.customerId}
          isOpen={isCustomerSideBarModalOpen}
          onClose={() => setIsCustomerSideBarModalOpen(false)}
          loadingTitle={getFullName(appointment.customer)}
          onCustomerSave={refresh}
          onSelectAppointment={onClose}
        />
      )}

      {isRefundDepositModalOpen && depositTransaction && (
        <TransactionRefundModal
          transaction={depositTransaction}
          open={isRefundDepositModalOpen}
          onClose={() => setIsRefundDepositModalOpen(false)}
          onRefresh={refresh}
        />
      )}
    </>
  );
}

export default AppointmentSideBarModal;
